import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar/Sidebar';
import Chat from './Chat/Chat';
import './MainApp.css';
import { Lang, t } from '../../i18n';
import { Conversation } from './Chat/Conversation';
import { sqlApi, StoredConversationRow, AssignementRow } from '../../services/sqlApi';
import type { ConversationObj } from './Chat/types';

/** Helper to generate a random conversation ID */
function randomId() {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

/** Helper to create a new conversation */
function createNewConversation(
  lang: Lang,
  assignementId?: number | null,
  assignementAddress?: string | null
) {
  const newId = randomId();
  const newConv = new Conversation(newId);
  if (assignementId != null && assignementId !== undefined) {
    newConv.assignementId = assignementId;
  }
  if (assignementAddress != null && assignementAddress !== undefined && assignementAddress !== '') {
    newConv.assignementAddress = assignementAddress;
  }
  newConv.addSystemMessage(t(lang, 'assistantWelcome'), 'assistantWelcome');
  return { newId, newConv };
}

/** Convert stored conversation JSON + row id into a Conversation instance (for sidebar) */
function hydrateStoredConversation(
  row: StoredConversationRow,
  addressByAssignementId: Map<number, string>
): Conversation {
  const c = row.conversation as Record<string, unknown>;
  const time = c.time != null ? new Date(c.time as string) : new Date();
  const messages = Array.isArray(c.messages)
    ? (c.messages as Array<Record<string, unknown>>).map((m) => ({
        ...m,
        timestamp: m.timestamp != null ? new Date(m.timestamp as string) : new Date(),
      }))
    : [];
  const assignementId =
    (row.assignement_id as number | null | undefined) ??
    (c.assignementId as number | null | undefined) ??
    null;
  const assignementAddress =
    assignementId != null
      ? addressByAssignementId.get(assignementId) ?? (c.assignementAddress as string | null) ?? null
      : (c.assignementAddress as string | null) ?? null;

  const obj: ConversationObj = {
    id: (c.id as string) || String(row.id),
    time,
    messages: messages as ConversationObj['messages'],
    tasks: (c.tasks as ConversationObj['tasks']) ?? [],
    isSubmitted: true,
    isCancelled: (c.isCancelled as boolean) ?? false,
    savedConversationId: row.id,
    conversationDialogueState: (c.conversationDialogueState as ConversationObj['conversationDialogueState']) ?? 'conv_bye',
    assignementId,
    assignementAddress,
  };
  return Conversation.fromConversationObj(obj);
}

/** Chats without SQL assignement_id are treated as belonging to the first open opdracht (Opdracht 1). */
function attachOrphansToPrimaryAssignement(
  convs: Conversation[],
  primary: { id: number; address: string } | null | undefined
): void {
  if (!primary) return;
  for (const c of convs) {
    if (c.assignementId == null) {
      c.assignementId = primary.id;
      c.assignementAddress = primary.address;
    }
  }
}

function firstOpenAssignement(rows: AssignementRow[]): AssignementRow | undefined {
  const open = rows.filter((a) => !a.is_completed).sort((a, b) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
  return open[0];
}

/** Today's date in YYYY-MM-DD (local) */
function getTodayDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface MainAppProps {
  currentUser: string;
  onLogout: () => void;
}

const MainApp: React.FC<MainAppProps> = ({ currentUser, onLogout }) => {
  const [lang, setLang] = useState<Lang>('nl');
  const [assignements, setAssignements] = useState<AssignementRow[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [currentConversation, setCurrentConversation] = useState<Conversation | undefined>(undefined);
  const [conversationUpdateTrigger, setConversationUpdateTrigger] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [assignementModalOpen, setAssignementModalOpen] = useState(false);
  const [assignementModalReason, setAssignementModalReason] = useState<
    'bootstrap' | 'new' | 'after_complete' | null
  >(null);
  const [assignementAddressDraft, setAssignementAddressDraft] = useState('');
  const [expandedAssignementOverrides, setExpandedAssignementOverrides] = useState<Record<number, boolean>>({});

  const pendingConversationRowsRef = useRef<StoredConversationRow[] | null>(null);
  const employeeIdRef = useRef<number | null>(null);
  const assignementsRef = useRef<AssignementRow[]>([]);
  assignementsRef.current = assignements;

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      if (newIsMobile !== isMobile) {
        setSidebarOpen(!newIsMobile);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  useEffect(() => {
    if (assignementModalOpen) {
      setAssignementAddressDraft('');
    }
  }, [assignementModalOpen]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentUser) return;
      /** Set once we match a row in `employees`; do not clear on later API errors (assignements/conversations). */
      let matchedEmployeeId: number | null = null;
      try {
        const employees = await sqlApi.getAllEmployees();
        const usernameParts = currentUser.split('_');
        const employee = employees.find(
          (emp: { name: string; surname: string }) =>
            emp.name.toLowerCase() === usernameParts[0]?.toLowerCase() &&
            emp.surname.toLowerCase() === usernameParts.slice(1).join('_').toLowerCase()
        );
        if (!employee) {
          employeeIdRef.current = null;
          const { newId, newConv } = createNewConversation(lang);
          setAssignements([]);
          setConversations([newConv]);
          setSelectedConversationId(newId);
          return;
        }
        matchedEmployeeId = employee.id;
        employeeIdRef.current = employee.id;
        const date = getTodayDateString();
        const [aRows, cRows] = await Promise.all([
          sqlApi.getAssignements({ date, employee_id: employee.id }),
          sqlApi.getConversations({ date, employee_id: employee.id }),
        ]);
        if (cancelled) return;
        setAssignements(aRows);
        const addressMap = new Map(aRows.map((a) => [a.id, a.address]));
        const openAssignements = aRows.filter((a) => !a.is_completed);
        if (openAssignements.length === 0) {
          pendingConversationRowsRef.current = cRows;
          setAssignementModalReason('bootstrap');
          setAssignementModalOpen(true);
          setConversations([]);
          setSelectedConversationId(null);
          return;
        }
        const primary = firstOpenAssignement(aRows)!;
        const hydrated = cRows.map((row) => hydrateStoredConversation(row, addressMap));
        attachOrphansToPrimaryAssignement(hydrated, primary);
        const { newId, newConv } = createNewConversation(lang, primary.id, primary.address);
        setConversations([...hydrated, newConv]);
        setSelectedConversationId(newId);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to load today’s data, starting with new conversation:', err);
        if (matchedEmployeeId != null) {
          employeeIdRef.current = matchedEmployeeId;
        } else {
          employeeIdRef.current = null;
        }
        const { newId, newConv } = createNewConversation(lang);
        setAssignements([]);
        setConversations([newConv]);
        setSelectedConversationId(newId);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    const conversation = conversations.find((c) => c.id === selectedConversationId);
    setCurrentConversation(conversation);
  }, [selectedConversationId, conversations, conversationUpdateTrigger]);

  const isAssignementGroupExpanded = (a: AssignementRow) => {
    if (expandedAssignementOverrides[a.id] !== undefined) {
      return expandedAssignementOverrides[a.id]!;
    }
    return !a.is_completed;
  };

  const toggleAssignementGroup = (id: number) => {
    const a = assignements.find((x) => x.id === id);
    const current = a ? isAssignementGroupExpanded(a) : true;
    setExpandedAssignementOverrides((o) => ({ ...o, [id]: !current }));
  };

  const handleAssignementAddressSubmit = async () => {
    const trimmed = assignementAddressDraft.trim();
    if (!trimmed) return;
    const empId = employeeIdRef.current;
    if (!empId) {
      setAssignementModalOpen(false);
      setAssignementModalReason(null);
      return;
    }
    const reason = assignementModalReason;
    try {
      const res = await sqlApi.createAssignement({ employee_id: empId, address: trimmed });
      const mergedA = [...assignementsRef.current, res.assignement];
      setAssignements(mergedA);
      const addrMap = new Map(mergedA.map((x) => [x.id, x.address]));

      if (reason === 'bootstrap') {
        const pending = pendingConversationRowsRef.current ?? [];
        pendingConversationRowsRef.current = null;
        const hydrated = pending.map((row) => hydrateStoredConversation(row, addrMap));
        attachOrphansToPrimaryAssignement(hydrated, { id: res.assignement_id, address: trimmed });
        const { newId, newConv } = createNewConversation(lang, res.assignement_id, trimmed);
        setConversations([...hydrated, newConv]);
        setSelectedConversationId(newId);
      } else {
        const { newId, newConv } = createNewConversation(lang, res.assignement_id, trimmed);
        setConversations((prev) => [...prev, newConv]);
        setSelectedConversationId(newId);
      }
      setAssignementModalOpen(false);
      setAssignementModalReason(null);
    } catch (e) {
      console.error(e);
      alert(lang === 'nl' ? 'Kon opdracht niet aanmaken.' : 'Could not create assignment.');
    }
  };

  const resolveEmployeeIdFromCurrentUser = async (): Promise<number | null> => {
    if (employeeIdRef.current != null) return employeeIdRef.current;
    if (!currentUser) return null;
    try {
      const employees = await sqlApi.getAllEmployees();
      const usernameParts = currentUser.split('_');
      const employee = employees.find(
        (emp: { name: string; surname: string }) =>
          emp.name.toLowerCase() === usernameParts[0]?.toLowerCase() &&
          emp.surname.toLowerCase() === usernameParts.slice(1).join('_').toLowerCase()
      );
      if (employee) {
        employeeIdRef.current = employee.id;
        return employee.id;
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  const handleRequestNewAssignement = async () => {
    const empId = await resolveEmployeeIdFromCurrentUser();
    if (empId == null) {
      alert(
        lang === 'nl'
          ? 'Je werkgever-profiel is niet gekoppeld aan je login. Gebruik een gebruikersnaam als voornaam_achternaam (zoals in het medewerkersregister), of neem contact op met een beheerder.'
          : 'Your login is not linked to an employee record. Use a firstname_lastname username (as in the employee list), or ask an administrator.'
      );
      return;
    }
    setAssignementModalReason('new');
    setAssignementModalOpen(true);
  };

  const handleCompleteAssignement = async (id: number) => {
    try {
      await sqlApi.updateAssignement(id, { is_completed: true });
      setAssignements((prev) =>
        prev.map((a) => (a.id === id ? { ...a, is_completed: true, completed_at: new Date().toISOString() } : a))
      );
      setExpandedAssignementOverrides((o) => ({ ...o, [id]: false }));
      setAssignementModalReason('after_complete');
      setAssignementModalOpen(true);
    } catch (e) {
      console.error(e);
      alert(lang === 'nl' ? 'Kon opdracht niet voltooien.' : 'Could not complete assignment.');
    }
  };

  const handleNewConversationRequested = () => {
    const selected = conversations.find((c) => c.id === selectedConversationId);
    const primary = firstOpenAssignement(assignements);
    const aid = selected?.assignementId ?? primary?.id;
    const addr = selected?.assignementAddress ?? primary?.address;
    const { newId, newConv } = createNewConversation(
      lang,
      aid != null ? aid : undefined,
      addr != null && addr !== '' ? addr : undefined
    );
    setConversations((prev) => [...prev, newConv]);
    setSelectedConversationId(newId);
  };

  const triggerConversationUpdate = () => {
    setConversationUpdateTrigger((prev) => prev + 1);

    setTimeout(() => {
      const messagesEndRef = document.querySelector('[data-messages-end]') as HTMLDivElement;
      if (messagesEndRef) {
        messagesEndRef.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="main-app">
      <button
        className={`sidebar-toggle ${sidebarOpen ? 'sidebar-open' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle sidebar"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor" />
        </svg>
      </button>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      {assignementModalOpen && (
        <div
          className="assignement-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="assignement-modal-title"
        >
          <div className="assignement-modal">
            <h3 id="assignement-modal-title">{t(lang, 'assignementAddressTitle')}</h3>
            <p className="assignement-modal-hint">{t(lang, 'assignementAddressPrompt')}</p>
            <input
              type="text"
              className="assignement-modal-input"
              value={assignementAddressDraft}
              onChange={(e) => setAssignementAddressDraft(e.target.value)}
              placeholder={t(lang, 'assignementAddressPlaceholder')}
              autoFocus
            />
            <div className="assignement-modal-actions">
              {assignementModalReason !== 'bootstrap' && (
                <button
                  type="button"
                  className="assignement-modal-btn secondary"
                  onClick={() => {
                    setAssignementModalOpen(false);
                    setAssignementModalReason(null);
                  }}
                >
                  {t(lang, 'assignementCancel')}
                </button>
              )}
              <button type="button" className="assignement-modal-btn primary" onClick={handleAssignementAddressSubmit}>
                {t(lang, 'assignementSaveAddress')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Sidebar
        currentUser={currentUser}
        onLogout={onLogout}
        lang={lang}
        assignements={assignements}
        conversations={conversations}
        setSelectedConversationId={setSelectedConversationId}
        selectedConversationId={selectedConversationId}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onRequestNewAssignement={handleRequestNewAssignement}
        onCompleteAssignement={handleCompleteAssignement}
        isAssignementGroupExpanded={isAssignementGroupExpanded}
        onToggleAssignementGroup={toggleAssignementGroup}
      />
      <Chat
        currentUser={currentUser}
        lang={lang}
        setLang={setLang}
        currentConversation={currentConversation}
        onNewConversationRequested={handleNewConversationRequested}
        triggerConversationUpdate={triggerConversationUpdate}
        setConversations={setConversations}
        onLogout={onLogout}
      />
    </div>
  );
};

export default MainApp;
